import { apiClient } from "./apiClient";

/** C1: the unified clients roster — contract ⋈ tenant ⋈ workspace. */

export interface RosterTenant {
  tenant_id: string;
  workspace: { id: string; name: string | null } | null;
  /**
   * tenant_settings.status. Optional because older backend deploys omit it;
   * the /clients page uses it to render a per-row status pill.
   */
  status?: string | null;
}

export interface RosterClient {
  contract_id: string | null;
  name: string | null;
  website: string | null;
  monthly_points: number | null;
  monthly_budget_dollars: number | null;
  billing_cycle_start_day: number | null;
  is_paused: boolean;
  archived: boolean;
  tenants: RosterTenant[];
  ads_customer_id: string | null;
  flags: string[];
}

export interface RosterResponse {
  clients: RosterClient[];
  gaps: Record<string, number>;
}

export interface ClientDetail extends RosterClient {
  start_date: string | null;
  actual_start_date: string | null;
  pacing_carryover_start: string | null;
  agile_pays_ad_spend: boolean | null;
  main_poc: string | null;
  contact_email: string | null;
  client_email: string | null;
  client_phone: string | null;
  teamwork_project_id: string | null;
  teamwork_project_name: string | null;
  wc_profile_id: string | null;
  wc_account_name: string | null;
  lsa_customer_id: string | null;
  source_synced_at: string | null;
}

/** C4: per-tenant integration status (read-only during the dual-run). */
export interface TenantIntegrations {
  tenant_id: string;
  google_ads: {
    level: "tenant" | "agency" | null;
    status: string | null;
    customer_id: string | null;
    login_customer_id: string | null;
    external_account_id: string | null;
    last_health_check_at: string | null;
    last_error: string | null;
  };
  wordpress: { connected: boolean; partial_creds: boolean };
  google_docs: { enabled: boolean; folder_set: boolean };
  bing: { key_set: boolean };
  other_connections: {
    provider: string;
    status: string | null;
    external_account_id: string | null;
  }[];
}

export interface ClientIntegrationsResponse {
  contract_id: string;
  tenants: TenantIntegrations[];
}

/** C5: per-tenant workspace membership rollup (read-only). */
export interface WorkspaceMember {
  email: string;
  display_name: string | null;
  full_name: string | null;
  role: "owner" | "admin" | "editor" | "viewer";
  joined_at: string | null;
}

export interface TenantWorkspace {
  tenant_id: string;
  workspace: {
    id: string;
    name: string | null;
    slug: string;
    website: string | null;
    is_active: boolean;
    members: WorkspaceMember[];
  } | null;
}

export interface ClientWorkspacesResponse {
  tenants: TenantWorkspace[];
}

export const clientsService = {
  async roster(includeArchived = false): Promise<RosterResponse> {
    const { data } = await apiClient.get<RosterResponse>("/clients", {
      params: { include_archived: includeArchived },
    });
    return data;
  },

  async detail(contractId: string): Promise<ClientDetail> {
    const { data } = await apiClient.get<ClientDetail>(`/clients/${contractId}`);
    return data;
  },

  async integrations(contractId: string): Promise<ClientIntegrationsResponse> {
    const { data } = await apiClient.get<ClientIntegrationsResponse>(
      `/clients/${contractId}/integrations`,
    );
    return data;
  },

  async workspaces(contractId: string): Promise<ClientWorkspacesResponse> {
    const { data } = await apiClient.get<ClientWorkspacesResponse>(
      `/clients/${contractId}/workspaces`,
    );
    return data;
  },

  async leads(contractId: string): Promise<ClientLeadsResponse> {
    const { data } = await apiClient.get<ClientLeadsResponse>(
      `/clients/${contractId}/leads`,
    );
    return data;
  },

  // C6 contract authoring — Planner-first on the backend; flag-gated
  // (PLANNER_CONTRACT_AUTHORING_ENABLED), a 503 detail says it's off.
  // New clients go through createClient (below); the old create() is gone (R12).

  // Feature C "New client" (design §3, §6; R9, R12, R19).
  // The website goes raw: the server normalises it. confirm_ads_armed is
  // always sent (false unless ticked). 75 s timeout for this call only: the
  // worst case (preflight + POST + archive + re-PATCH) exceeds apiClient's 30 s.
  async createClient(draft: NewClientDraft): Promise<NewClientResult> {
    const { data } = await apiClient.post<NewClientResult>(
      "/clients",
      { ...draft, confirm_ads_armed: draft.confirm_ads_armed ?? false },
      { timeout: 75_000 },
    );
    return data;
  },

  // Read-only preview: what a create would do for this website (R9).
  async resolveClient(website: string): Promise<ResolveResult> {
    const { data } = await apiClient.get<ResolveResult>("/clients/resolve", {
      params: { website },
    });
    return data;
  },

  // Read-only "is this Google Ads account in our MCC?" check (Task 2). The
  // config GET answers 503 when the check is off here; the card reads that off
  // the rejection, so errors are propagated untouched. Nothing is saved.
  async adsMccCheckConfig(): Promise<MccCheckConfig> {
    const { data } = await apiClient.get<MccCheckConfig>("/clients/ads-mcc-check");
    return data;
  },

  async checkAdsMcc(customerId: string): Promise<MccCheckResult> {
    const { data } = await apiClient.post<MccCheckResult>("/clients/ads-mcc-check", {
      customer_id: customerId,
    });
    return data;
  },

  // Active AgileCo staff for the members picker (R15), at most 50.
  async staffUsers(search?: string): Promise<StaffUser[]> {
    const term = search?.trim();
    const { data } = await apiClient.get<{ users: StaffUser[] }>(
      "/clients/staff-users",
      term ? { params: { search: term } } : undefined,
    );
    return data.users;
  },

  async update(contractId: string, payload: Partial<ContractDraft>): Promise<void> {
    await apiClient.patch(`/clients/${contractId}`, payload);
  },

  // S3: Planner-first connection + manual spend writes.
  async setConnection(
    contractId: string,
    platform: string,
    externalCustomerId: string | null,
  ): Promise<void> {
    await apiClient.put(`/clients/${contractId}/connections`, {
      platform,
      external_customer_id: externalCustomerId,
    });
  },

  async recordSpend(contractId: string, payload: ManualSpendDraft): Promise<void> {
    await apiClient.put(`/clients/${contractId}/spend`, payload);
  },

  // Platform split: let Core decide per-platform allotments from run rate.
  // GET is read-only; PATCH/POST are staff actions. A 409 on GET means the
  // client has no funded platforms (an empty state, not an error).
  async pacingSplit(contractId: string): Promise<PacingSplit> {
    const { data } = await apiClient.get<PacingSplit>(`/clients/${contractId}/pacing-split`);
    return data;
  },

  async setPacingSplit(contractId: string, autoSplit: boolean): Promise<PacingSplitSetting> {
    const { data } = await apiClient.patch<PacingSplitSetting>(
      `/clients/${contractId}/pacing-split`,
      { auto_split: autoSplit },
    );
    return data;
  },

  async applyPacingSplit(contractId: string): Promise<PacingSplitApplyResult> {
    const { data } = await apiClient.post<PacingSplitApplyResult>(
      `/clients/${contractId}/pacing-split/apply`,
    );
    return data;
  },

  async lifecycle(contractIds: string[], action: LifecycleAction): Promise<LifecycleResult> {
    const { data } = await apiClient.post<LifecycleResult>("/clients/lifecycle", {
      contract_ids: contractIds,
      action,
    });
    return data;
  },
};

/** W3: per-client WhatConverts leads from Core's own cache. */
export interface WcLead {
  id: string;
  wc_lead_id: string | null;
  lead_type: string | null;
  lead_status: string | null;
  date_created: string | null;
  lead_source: string | null;
  lead_medium: string | null;
  contact_name: string | null;
  contact_company: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  quotable: string | null;
  quote_value: number | null;
  sales_value: number | null;
  /**
   * Core's own label reason: the Jev label at its current version, else the
   * shadow AI label (wc_lead_labels). null when neither has labelled it.
   * Older backends send ai_reason instead and omit these.
   */
  reason?: string | null;
  /** Which labeller the reason came from ("jev" | "ai_scorer"). */
  reason_labeler?: string | null;
}

export interface ClientLeadsResponse {
  summary: {
    total: number;
    quotable_yes: number;
    quote_value: number;
    sales_value: number;
  };
  leads: WcLead[];
  sync_enabled: boolean;
}

export interface ContractDraft {
  name: string;
  website?: string | null;
  monthly_points?: number | null;
  monthly_budget_dollars?: number | null;
  billing_cycle_start_day?: number | null;
  start_date?: string | null;
  main_poc?: string | null;
  contact_email?: string | null;
  client_email?: string | null;
  client_phone?: string | null;
}

/** POST /clients body for the New client action (design §3.1). Deliberately
 *  NOT part of ContractDraft, so create-only keys can never reach the edit
 *  PATCH (R12). Contract fields ride along as in ContractDraft. */
export interface NewClientDraft extends ContractDraft {
  website: string;
  services: "seo" | "ads" | "both";
  approval_required: boolean;
  confirm_ads_armed?: boolean;
  member_user_ids?: string[];
}

export interface NewClientWarning {
  code: "pacing_armed" | "tenant_paused" | "audit_not_recorded" | "access_cache_stale";
  message: string;
}

/** 201 body of POST /clients (design §3.1). */
export interface NewClientResult {
  contract_id: string;
  website: string;
  tenant: { id: string; created: boolean };
  workspace: { id: string; name: string; created: boolean };
  members_added: string[];
  product_package: { old: string; new: string; changed: boolean };
  approval_required: boolean;
  ads_armed: boolean;
  warnings: NewClientWarning[];
}

export interface ResolveConflict {
  code: string;
  message: string;
  [key: string]: unknown;
}

/** GET /clients/resolve body (design §3.2). */
export interface ResolveResult {
  website: string;
  tenant: { id: string; stored_website: string; status: string } | null;
  workspace: { id: string; name: string } | null;
  will_create: { tenant: boolean; workspace: boolean };
  conflict: ResolveConflict | null;
  ads_armed: boolean;
}

/** GET /clients/ads-mcc-check: which MCC this environment checks against. */
export interface MccCheckConfig {
  mcc_id: string | null;
  mcc_id_formatted: string | null;
  env: string;
}

export type MccCheckUnavailableReason =
  | "google_ads_error"
  | "error"
  | "no_token"
  | "not_configured"
  | "disabled";

/** POST /clients/ads-mcc-check body: linked / not_linked / unavailable. */
export interface MccCheckResult extends MccCheckConfig {
  state: "linked" | "not_linked" | "unavailable";
  checked_customer_id: string;
  /** Only when state is "linked". */
  account?: { name: string; status: string; level: number; manager: boolean };
  /** Only when the typed id is our own MCC. */
  hint?: "own_mcc";
  /** Only when state is "unavailable". */
  reason?: MccCheckUnavailableReason;
}

/** GET /clients/staff-users row (design §3.3). */
export interface StaffUser {
  id: string;
  email: string;
  display_name: string | null;
}

export type LifecycleAction = "pause" | "unpause" | "archive" | "unarchive";

export interface LifecycleResult {
  action: LifecycleAction;
  done: string[];
  errors: { contract_id: string; error: string }[];
}

export interface PacingSplitSetting {
  auto_split: boolean;
  set_by: string | null;
  set_at: string | null;
}

export interface PacingSplit {
  total: number;
  current: Record<string, number>;
  proposed: Record<string, number>;
  ideal: Record<string, number>;
  shares: Record<string, number>;
  capped: boolean;
  sink: string;
  basis_cycles: number;
  trailing_avg_spend: Record<string, number | null>;
  setting: PacingSplitSetting;
}

export interface PacingSplitApplyResult extends PacingSplit {
  written: Record<string, number>;
  frozen?: Record<string, string[]>;
  billing_period_start?: string;
  skipped?: string;
}

export interface ManualSpendDraft {
  platform: string;
  billing_period_start: string;
  billing_period_end?: string;
  spend_dollars: number;
  budget_dollars?: number | null;
}

export const MANUAL_SPEND_PLATFORMS = [
  "Meta Ads",
  "Bing",
  "Reddit Ads",
  "LinkedIn Ads",
  "Agile Work",
] as const;

export const CONNECTION_PLATFORMS = [
  { key: "google_ads", label: "Google Ads" },
  { key: "google_lsa", label: "Google LSA" },
  { key: "meta_ads", label: "Meta Ads" },
  { key: "bing", label: "Bing" },
] as const;
